import { formatTime } from "@/lib/format-data";

import type { ProviderPresenceInfo } from "@/types/team-chat";

export type StaffPresenceRank = 0 | 1 | 2 | 3 | 4;

export interface FormatTransferPresenceArgs {
  rank: StaffPresenceRank;
  status: "ONLINE" | "WITH_CLIENT" | "BUSY" | undefined;
  presence: ProviderPresenceInfo | undefined;
  firstName: string;
  lastName: string;
  timeZone: string;
  now?: Date;
}

export interface FormatTransferPresenceResult {
  label: string;
  tooltip: string;
  deemphasize: boolean;
}

function formatEndTime(
  endTime: string | undefined | null,
  timeZone: string,
): string | null {
  if (!endTime) return null;
  const date = new Date(endTime);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return formatTime(date, true, timeZone);
  } catch {
    // Invalid IANA timezone (user-supplied timezoneOverride can be anything)
    // throws RangeError. Fall back to the viewer's own zone rather than crash
    // the dialog.
    return formatTime(date);
  }
}

function pickCustom(
  presence: ProviderPresenceInfo | undefined,
  now: Date,
): { emoji: string | null; text: string | null } | null {
  if (!presence) return null;
  const { customStatusEmoji, customStatusText, customStatusExpiresAt } =
    presence;
  if (customStatusExpiresAt) {
    const expiry = new Date(customStatusExpiresAt);
    if (!Number.isNaN(expiry.getTime()) && expiry <= now) return null;
  }
  const emoji =
    customStatusEmoji && customStatusEmoji.trim() ? customStatusEmoji : null;
  const text =
    customStatusText && customStatusText.trim() ? customStatusText : null;
  if (!emoji && !text) return null;
  return { emoji, text };
}

export function formatTransferPresence({
  rank,
  status,
  presence,
  firstName,
  lastName,
  timeZone,
  now,
}: FormatTransferPresenceArgs): FormatTransferPresenceResult {
  const fullName = `${firstName} ${lastName}`.trim();
  const effectiveNow = now ?? new Date();

  if (rank === 3) {
    return {
      label: "Offline",
      tooltip: fullName ? `${fullName} — Offline` : "Offline",
      deemphasize: true,
    };
  }

  if (rank === 4) {
    return {
      label: "",
      tooltip: fullName,
      deemphasize: true,
    };
  }

  // Ranks 0/1/2 — custom status overrides calendar labels (mirrors
  // the Whisper chat's PresenceStatus component).
  const custom = pickCustom(presence, effectiveNow);
  if (custom) {
    const label = [custom.emoji, custom.text].filter(Boolean).join(" ");
    return { label, tooltip: label, deemphasize: false };
  }

  if (status === "WITH_CLIENT") {
    const label = "🩺 With patient";
    const endTime = formatEndTime(
      presence?.currentAppointment?.endTime,
      timeZone,
    );
    return {
      label,
      tooltip: endTime ? `🩺 With patient until ${endTime}` : label,
      deemphasize: false,
    };
  }

  if (status === "BUSY") {
    const label = "Busy";
    const endTime = formatEndTime(presence?.currentBlock?.endTime, timeZone);
    return {
      label,
      tooltip: endTime ? `Busy until ${endTime}` : label,
      deemphasize: false,
    };
  }

  return { label: "Online", tooltip: "Online", deemphasize: false };
}
